import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";
import { BuyButton } from "./BuyButton";
import { Price } from "./Price";
import { LeadCapture } from "./LeadCapture";
import { track } from "@/lib/track";

const QUESTIONS = [
  { q: "A product is all over your feed this week. What do you do?", a: [
    ["Order stock and start selling it right away", 0], ["Check if interest is really growing or just spiking", 2], ["Save it and never look at it again", 1]] },
  { q: "How do you usually pick a business idea?", a: [
    ["Whatever successful creators recommend", 0], ["Whatever feels exciting right now", 1], ["Patterns in what people need and complain about", 2]] },
  { q: "A trend you follow suddenly drops for two days. You…", a: [
    ["Drop it immediately", 0], ["Keep going, no matter what", 1], ["Look at the longer trend before deciding", 2]] },
  { q: "Where does most of your information come from?", a: [
    ["One or two social media feeds", 0], ["Friends and family", 1], ["A mix: news, forums, real customers", 2]] },
  { q: "You found a promising idea. What happens next?", a: [
    ["I wait to see if others succeed with it first", 0], ["I research until I feel completely ready", 1], ["I run a small test this week", 2]] },
];

const RESULTS = [
  { max: 4, type: "Noise follower", title: "You are reacting to the loudest voice in the room.",
    text: "Right now the market's static decides for you. That is why ideas feel crowded by the time you get there. Chapter 2, Understanding Market Data Noise, is written exactly for this point." },
  { max: 7, type: "Signal seeker", title: "You can hear the signal. You just don't trust it yet.",
    text: "Your instincts are good, but without a way to score a signal you hesitate or act too late. Chapter 3 and the Urgency Assessment Worksheet show you when it is time to move." },
  { max: 10, type: "Signal-driven", title: "You think like a first mover. Now make it a system.",
    text: "You already look past the hype. What is missing is a repeatable process to turn signals into decisive moves. Chapter 4, Action Activation, and the Action Planning Blueprint close that gap." },
];

const MAX = QUESTIONS.length * 2;

const Result = ({ score, onRestart }) => {
  const r = RESULTS.find((x) => score <= x.max);
  const pct = Math.round((score / MAX) * 100);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="quiz-result">
      <p className="eyebrow">Your result</p>
      <p className="font-display mt-4 text-sm uppercase tracking-[0.2em] text-cta" data-testid="quiz-result-type">{r.type}</p>
      <h3 className="font-display mt-3 text-3xl font-medium leading-tight tracking-tight text-white md:text-4xl">{r.title}</h3>
      <div className="mt-8">
        <div className="flex justify-between text-[11px] uppercase tracking-widest text-zinc-400"><span>Noise</span><span>Signal</span></div>
        <div className="relative mt-2 h-1.5 bg-[#1f1f1f]">
          <motion.div className="h-full bg-cta" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
        </div>
        <p className="mt-2 text-sm text-zinc-300" data-testid="quiz-score">{score} of {MAX} signal points</p>
      </div>
      <p className="mt-8 max-w-xl leading-relaxed text-zinc-300">{r.text}</p>
      <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
        <BuyButton role="bundle" testId="quiz-buy-button">Get the Bundle, <Price role="bundle" /></BuyButton>
        <Link to="/products/bundle" className="link-u text-sm text-zinc-300 hover:text-white" data-testid="quiz-inside-link">See what is inside</Link>
      </div>
      <div className="mt-12 max-w-xl">
        <p className="mb-4 text-sm text-zinc-300">Or get 10% off by email and decide later.</p>
        <LeadCapture source={`quiz-${r.type.toLowerCase().replace(/\s/g, "-")}`} />
      </div>
      <button onClick={onRestart} className="mt-10 flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="quiz-restart"><RotateCcw size={14} /> Take the quiz again</button>
    </motion.div>
  );
};

export const SignalQuiz = () => {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const done = step >= QUESTIONS.length;

  const answer = (points) => {
    if (step === 0) track("quiz_start", {});
    const total = score + points;
    setScore(total);
    setStep(step + 1);
    if (step + 1 === QUESTIONS.length) track("quiz_complete", { score: total });
  };
  const restart = () => { setStep(0); setScore(0); };

  if (done) return <Result score={score} onRestart={restart} />;
  const q = QUESTIONS[step];
  return (
    <div data-testid="quiz-question">
      <div className="flex items-center gap-4">
        <span className="font-display text-sm text-white" data-testid="quiz-progress">{step + 1} / {QUESTIONS.length}</span>
        <div className="flex flex-1 gap-1.5">
          {QUESTIONS.map((_, i) => <span key={i} className={`h-1 flex-1 transition-colors duration-500 ${i <= step ? "bg-cta" : "bg-[#1f1f1f]"}`} />)}
        </div>
      </div>
      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
        <h3 className="font-display mt-10 text-2xl font-medium leading-snug text-white md:text-4xl">{q.q}</h3>
        <div className="mt-8 grid gap-3">
          {q.a.map(([label, pts], i) => (
            <button key={label} onClick={() => answer(pts)} data-testid={`quiz-answer-${i}`}
              className="group flex items-center justify-between border border-line bg-[#0b0b0b] px-6 py-5 text-left text-zinc-200 transition-colors hover:border-cta hover:text-white">
              <span className="flex items-center gap-4">
                <span className="font-display text-xs text-zinc-500 group-hover:text-cta">{String.fromCharCode(65 + i)}</span>{label}
              </span>
              <ArrowRight size={16} className="shrink-0 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
};
